import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ChefHat,
  Lock,
  Plus,
  Minus,
  Trash2,
  LogOut,
  ShoppingBag,
  Search,
  Check,
  Clock,
  CheckCircle2,
  AlertCircle,
  Utensils,
  Hand,
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney, fmtTimeAgo, useRealtime, usePolling } from '@/lib/realtime';
import { KioskLockBar } from '@/lib/kioskLock';

/**
 * Waiter Mode — table-side service iPad app.
 *
 * Workflow:
 *  1) Waiter signs in. They see a grid of their branch's tables.
 *  2) Tapping a Free table opens the menu picker and starts a new dine-in order.
 *  3) Tapping a Locked-by-me table reopens the in-progress order to add items.
 *  4) Locked-by-other tables are disabled and show the owning waiter's name.
 *  5) Waiter's order status updates in real-time and a chime fires on Ready.
 *  6) Once handed off, waiter taps "Mark served" to close the order.
 */

// Plays a short chime so the waiter notices Ready transitions from across
// the room. Falls back silently if AudioContext isn't available.
function playReadyChime() {
  try {
    // eslint-disable-next-line no-undef
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = 'sine';
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.18, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    o.start();
    o.stop(ctx.currentTime + 0.5);
  } catch (e) { /* noop */ }
}

const STATUS_LABEL = {
  pending_payment: 'Pending payment',
  received: 'Received',
  preparing: 'Cooking',
  ready: 'Ready to serve',
  completed: 'Served',
  cancelled: 'Cancelled',
};

const STATUS_BADGE = {
  received: 'bg-amber-100 text-amber-900 border-amber-300',
  preparing: 'bg-blue-100 text-blue-900 border-blue-300',
  ready: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  completed: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  cancelled: 'bg-rose-100 text-rose-700 border-rose-300',
  pending_payment: 'bg-purple-100 text-purple-900 border-purple-300',
};

export default function WaiterMode() {
  const { user, logout } = useAuth();
  const isWaiter = user?.role === 'waiter';

  const [tables, setTables] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTable, setActiveTable] = useState(null); // { ...table }
  const [cart, setCart] = useState([]);
  const [specialInstr, setSpecialInstr] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [servingOrder, setServingOrder] = useState(null); // confirm-served dialog
  const lastReadyIdsRef = useRef(new Set());

  const loadTables = useCallback(async () => {
    try {
      const { data } = await api.get('/waiter/tables');
      setTables(data || []);
    } catch (e) {
      // Only show the toast on first failure to avoid spamming on poll
      if (!tables.length) toast.error('Failed to load tables');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMyOrders = useCallback(async () => {
    if (!isWaiter) { setMyOrders([]); return; }
    try {
      const { data } = await api.get('/waiter/orders/mine');
      setMyOrders(data || []);
      // Detect new "ready" transitions and chime
      const readyNow = new Set((data || []).filter((o) => o.status === 'ready').map((o) => o.id));
      const previouslyReady = lastReadyIdsRef.current;
      const newlyReady = [...readyNow].filter((id) => !previouslyReady.has(id));
      if (newlyReady.length) {
        playReadyChime();
        newlyReady.forEach((id) => {
          const o = (data || []).find((x) => x.id === id);
          if (o) toast.success(`\ud83c\udf7d\ufe0f Order #${o.order_number} ready to serve — Table ${o.table_number ?? '?'}`, { duration: 12000 });
        });
      }
      lastReadyIdsRef.current = readyNow;
    } catch (e) { /* silent on poll */ }
  }, [isWaiter]);

  const loadMenu = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([api.get('/menu/items'), api.get('/menu/categories')]);
      setMenuItems(m.data || []);
      setCategories(c.data || []);
    } catch (e) { toast.error('Failed to load menu'); }
  }, []);

  useEffect(() => { loadTables(); loadMyOrders(); loadMenu(); }, [loadTables, loadMyOrders, loadMenu]);

  // Realtime: subscribe to branch channel so we get push updates on order
  // status transitions and new orders that affect our table grid.
  const branchId = user?.branch_id;
  useRealtime(
    branchId ? [`admin:${branchId}`] : [],
    useCallback(() => { loadTables(); loadMyOrders(); }, [loadTables, loadMyOrders]),
  );
  // Polling fallback (WebSocket may be flaky on iPad in PWA mode)
  usePolling(() => { loadTables(); loadMyOrders(); }, 10000, []);

  const filteredMenu = useMemo(() => {
    const s = search.trim().toLowerCase();
    return menuItems.filter((m) =>
      m.is_available !== false
      && (activeCat === 'all' || m.category_id === activeCat)
      && (!s || m.name.toLowerCase().includes(s) || (m.description || '').toLowerCase().includes(s)),
    );
  }, [menuItems, activeCat, search]);

  const subtotal = cart.reduce((s, i) => s + (i.price || 0) * i.quantity, 0);

  const add = (m) => setCart((p) => {
    const ex = p.find((x) => x.menu_item_id === m.id);
    if (ex) return p.map((x) => x.menu_item_id === m.id ? { ...x, quantity: x.quantity + 1 } : x);
    return [...p, { menu_item_id: m.id, name: m.name, price: m.price, image_url: m.image_url, quantity: 1 }];
  });
  const inc = (id, by) => setCart((p) => p.map((x) => x.menu_item_id === id ? { ...x, quantity: Math.max(0, x.quantity + by) } : x).filter((x) => x.quantity > 0));
  const removeItem = (id) => setCart((p) => p.filter((x) => x.menu_item_id !== id));

  const openTable = (t) => {
    // Locked by another waiter — blocked
    if (t.lock && t.lock.active && !t.lock.owned_by_me) {
      toast.warning(`Table ${t.table_number} is currently being served by ${t.lock.server_name || 'another waiter'}`);
      return;
    }
    setActiveTable(t);
    setCart([]);
    setSpecialInstr('');
    setActiveCat('all');
    setSearch('');
  };

  const closeMenu = () => {
    setActiveTable(null);
    setCart([]);
    setSpecialInstr('');
  };

  const submitOrder = async () => {
    if (!activeTable || !cart.length) return;
    setSubmitting(true);
    try {
      const payload = {
        branch_id: branchId,
        order_type: 'dine_in',
        table_id: activeTable.id,
        items: cart.map((c) => ({ menu_item_id: c.menu_item_id, quantity: c.quantity })),
        payment_method: 'cash',
        customer_name: `Table ${activeTable.table_number}`,
        special_instructions: specialInstr.trim() || undefined,
      };
      const { data: order } = await api.post('/orders', payload);
      toast.success(`Order #${order.order_number} sent to kitchen \u2014 Table ${activeTable.table_number}`);
      closeMenu();
      loadTables();
      loadMyOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send order');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmServe = async () => {
    if (!servingOrder) return;
    try {
      await api.post(`/waiter/orders/${servingOrder.id}/serve`);
      toast.success(`Served table ${servingOrder.table_number} • #${servingOrder.order_number}`);
      setServingOrder(null);
      loadTables();
      loadMyOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark served');
    }
  };

  // Tables ordered: numerical first then alphanumeric
  const orderedTables = useMemo(() => {
    const list = [...tables];
    list.sort((a, b) => {
      const na = Number(a.table_number);
      const nb = Number(b.table_number);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return String(a.table_number || '').localeCompare(String(b.table_number || ''));
    });
    return list;
  }, [tables]);

  return (
    <div className="min-h-screen bg-background">
      <KioskLockBar title="Waiter" position="bottom-right" />
      <header data-testid="waiter-header" className="sticky top-0 z-20 border-b border-border bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div>
            <p className="text-xs uppercase tracking-wider opacity-80">Waiter mode</p>
            <h1 className="font-display text-2xl font-semibold">{user?.full_name || 'Waiter'}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" data-testid="waiter-active-count" className="bg-white/15 text-white">
              {myOrders.filter((o) => ['received', 'preparing', 'ready'].includes(o.status)).length} active
            </Badge>
            <Button data-testid="waiter-logout" size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={logout}>
              <LogOut className="mr-1 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[1fr_360px]">
        {/* Tables grid */}
        <section>
          <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold">
            <Utensils className="h-5 w-5 text-primary" /> Tables
          </h2>
          {!orderedTables.length && (
            <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No tables configured for this branch yet. Ask a manager to add tables in Branches → Tables.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {orderedTables.map((t) => {
              const lock = t.lock;
              const lockedByOther = !!(lock && !lock.owned_by_me);
              const lockedByMe = !!(lock && lock.owned_by_me);
              return (
                <Card
                  key={t.id}
                  data-testid={lockedByMe ? 'waiter-table-mine' : lockedByOther ? 'waiter-table-locked' : 'waiter-table-free'}
                  className={
                    'cursor-pointer overflow-hidden border-2 transition '
                    + (lockedByOther
                        ? 'border-zinc-300 bg-zinc-50 opacity-80 cursor-not-allowed'
                        : lockedByMe
                          ? 'border-emerald-400 bg-emerald-50/60 hover:border-emerald-500'
                          : 'border-border hover:border-primary hover:shadow-md')
                  }
                  onClick={() => openTable(t)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <p className="font-display text-2xl font-bold leading-none">#{t.table_number}</p>
                      {lockedByOther && <Lock className="h-4 w-4 text-zinc-500" />}
                      {lockedByMe && <Hand className="h-4 w-4 text-emerald-700" />}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">Seats {t.seats || t.capacity || 4}</p>
                    {lock?.active ? (
                      <div className="mt-2 space-y-1">
                        <Badge
                          variant="outline"
                          data-testid="waiter-table-status"
                          className={`text-[10px] ${STATUS_BADGE[lock.order_status] || ''}`}
                        >
                          {STATUS_LABEL[lock.order_status] || lock.order_status}
                        </Badge>
                        <p className="truncate text-[11px] text-muted-foreground">
                          #{lock.order_number} &middot; {lock.item_count} item{lock.item_count === 1 ? '' : 's'}
                        </p>
                        {lockedByOther && (
                          <p className="truncate text-[11px] font-medium text-zinc-700">
                            By {lock.server_name || 'another waiter'}
                          </p>
                        )}
                      </div>
                    ) : (
                      <Badge variant="outline" className="mt-2 border-emerald-200 bg-emerald-50 text-[10px] text-emerald-800">FREE</Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* My orders sidebar */}
        <aside>
          <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold">
            <ChefHat className="h-5 w-5 text-primary" /> My orders
          </h2>
          {!myOrders.length && (
            <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              You haven&apos;t taken any orders yet. Tap a free table to start.
            </p>
          )}
          <ScrollArea className="max-h-[70vh]">
            <ul className="space-y-2 pr-2">
              {myOrders.map((o) => (
                <li key={o.id}>
                  <Card data-testid="waiter-my-order-row" className={o.status === 'ready' ? 'border-2 border-emerald-400 bg-emerald-50/60' : ''}>
                    <CardContent className="space-y-2 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-display text-base font-semibold leading-none">Table {o.table_number}</p>
                          <p className="text-xs text-muted-foreground">#{o.order_number} &middot; {fmtTimeAgo(o.created_at)}</p>
                        </div>
                        <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[o.status] || ''}`}>
                          {STATUS_LABEL[o.status] || o.status}
                        </Badge>
                      </div>
                      <ul className="text-xs text-muted-foreground">
                        {(o.items || []).slice(0, 3).map((it, i) => (
                          <li key={i} className="truncate">{it.quantity}&times; {it.name}</li>
                        ))}
                        {(o.items || []).length > 3 && <li>+ {o.items.length - 3} more&hellip;</li>}
                      </ul>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Total</span>
                        <span className="tabular font-semibold">{fmtMoney(o.total, o.currency)}</span>
                      </div>
                      {o.status === 'ready' && (
                        <Button
                          data-testid="waiter-mark-served"
                          size="sm"
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => setServingOrder(o)}
                        >
                          <CheckCircle2 className="mr-1 h-4 w-4" /> Mark served
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>
          </ScrollArea>
        </aside>
      </div>

      {/* Order builder dialog */}
      <Dialog open={!!activeTable} onOpenChange={(o) => { if (!o) closeMenu(); }}>
        <DialogContent data-testid="waiter-order-dialog" className="max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Utensils className="h-5 w-5 text-primary" />
              {activeTable?.lock?.owned_by_me
                ? `Add to Table ${activeTable?.table_number} order`
                : `New order \u2014 Table ${activeTable?.table_number}`}
            </DialogTitle>
            <DialogDescription>
              {activeTable?.lock?.owned_by_me
                ? `Currently #${activeTable.lock.order_number} • ${activeTable.lock.item_count} item(s). Adding here creates a follow-up ticket.`
                : `Take the guest's order. The kitchen will see it instantly.`}
            </DialogDescription>
          </DialogHeader>

          <div className="grid flex-1 gap-4 overflow-hidden md:grid-cols-[160px_1fr_280px]">
            {/* Categories */}
            <div className="hidden flex-col gap-1 overflow-y-auto md:flex">
              <Button data-testid="waiter-cat-all" size="sm" variant={activeCat === 'all' ? 'default' : 'ghost'} className="justify-start" onClick={() => setActiveCat('all')}>All</Button>
              {categories.map((c) => (
                <Button
                  key={c.id}
                  data-testid="waiter-cat"
                  size="sm"
                  variant={activeCat === c.id ? 'default' : 'ghost'}
                  className="justify-start"
                  onClick={() => setActiveCat(c.id)}
                >{c.name}</Button>
              ))}
            </div>

            {/* Menu */}
            <div className="flex flex-col overflow-hidden">
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="waiter-menu-search" placeholder="Search menu…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 pl-8" />
              </div>
              <ScrollArea className="flex-1 pr-2">
                <ul className="grid gap-2 sm:grid-cols-2">
                  {filteredMenu.map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        data-testid="waiter-menu-item"
                        onClick={() => add(m)}
                        className="flex w-full items-center gap-3 rounded-md border border-border bg-card p-2 text-left transition hover:border-primary hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                      >
                        {m.image_url
                          ? <img src={m.image_url} alt="" className="h-12 w-12 shrink-0 rounded object-cover" />
                          : <div className="h-12 w-12 shrink-0 rounded bg-muted" />}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{m.name}</p>
                          <p className="text-xs text-muted-foreground tabular">{fmtMoney(m.price)}</p>
                        </div>
                        <Plus className="h-4 w-4 text-primary" />
                      </button>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            </div>

            {/* Cart */}
            <div className="flex flex-col overflow-hidden border-l border-border pl-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cart</p>
              <ScrollArea className="flex-1 pr-1">
                {!cart.length && <p className="py-4 text-xs text-muted-foreground">Tap items on the left to add them.</p>}
                <ul className="space-y-2">
                  {cart.map((i) => (
                    <li key={i.menu_item_id} data-testid="waiter-cart-row" className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{i.name}</p>
                        <p className="text-xs text-muted-foreground tabular">{fmtMoney(i.price)} each</p>
                      </div>
                      <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => inc(i.menu_item_id, -1)}><Minus className="h-3 w-3" /></Button>
                      <span className="w-5 text-center text-sm tabular">{i.quantity}</span>
                      <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => inc(i.menu_item_id, 1)}><Plus className="h-3 w-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => removeItem(i.menu_item_id)}><Trash2 className="h-3 w-3" /></Button>
                    </li>
                  ))}
                </ul>
              </ScrollArea>

              <div className="space-y-2 border-t border-border pt-2">
                <Textarea
                  data-testid="waiter-special-instructions"
                  rows={2}
                  placeholder="Special instructions (allergies, no onions, well-done…)"
                  value={specialInstr}
                  onChange={(e) => setSpecialInstr(e.target.value)}
                  className="text-xs"
                />
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular font-semibold">{fmtMoney(subtotal)}</span>
                </div>
                <Button
                  data-testid="waiter-send-order"
                  className="h-12 w-full text-base"
                  disabled={!cart.length || submitting}
                  onClick={submitOrder}
                >
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  {submitting ? 'Sending…' : `Send to kitchen • ${cart.length} item${cart.length === 1 ? '' : 's'}`}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm-served dialog */}
      <Dialog open={!!servingOrder} onOpenChange={(o) => { if (!o) setServingOrder(null); }}>
        <DialogContent data-testid="waiter-serve-confirm" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark order served?</DialogTitle>
            <DialogDescription>
              Confirm you handed order #{servingOrder?.order_number} to the guests at table {servingOrder?.table_number}. This closes the order.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setServingOrder(null)}>Cancel</Button>
            <Button data-testid="waiter-serve-confirm-button" className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmServe}>
              <Check className="mr-1 h-4 w-4" /> Yes, served
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
